import { ReactNode } from "react";
import { useOpenCookiePreferences, useResolvedRegime } from "@/lib/consent";
import { useT } from "@/lib/i18n";

/**
 * The link that opens the preference centre, used in the site footers, in
 * Settings, and in the privacy policy.
 *
 * Replaces the `.cky-banner-element` anchors CookieYes used to bind itself to.
 * Consent has to be as easy to withdraw as it was to give, so this link needs
 * to keep working on every surface: if it silently no-ops, the banner is no
 * longer lawful.
 *
 * In opt-out regions it relabels itself to "Your Privacy Choices". Using the
 * Meta pixel and Google Ads for targeted advertising is "sharing" under the
 * CPRA, which requires an opt-out link in those words (or "Do Not Sell or Share
 * My Personal Information") rather than a generic cookie-settings link. The
 * official opt-out icon is optional under § 7015(b) — it may accompany the
 * link, never replace it — so the text alone is the compliant baseline and no
 * approximation of a regulatory mark is drawn here.
 */
export function ManageCookiesLink({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const openPreferences = useOpenCookiePreferences();
  const regime = useResolvedRegime();
  const { t } = useT();

  // `children` until the region is known, so the label does not flip after
  // paint for the majority of visitors who see the default wording.
  const label = regime === "opt-out" ? t("cookies.privacyChoices") : children;

  return (
    <a
      href="#"
      className={className}
      onClick={(e) => {
        e.preventDefault();
        openPreferences();
      }}
    >
      {label}
    </a>
  );
}

export default ManageCookiesLink;
